/**
 * Onda VELAS — the canvas that READS THE WIND (client render-only; ZERO wire, ZERO sim).
 *
 * The hulls ship as Meshy concepts rebuilt in Blender: every rigged class (SLOOP/BRIGANTINE/GALLEON/FRIGATE)
 * carries a separate `Sails` node whose mesh is a GRID OF UNWELDED QUADS with ONE morph target — the furl
 * shape the renderer already scrubs off the sail trim. That baked morph is a POSE, not a response: at any
 * trim the canvas sat frozen in exactly the same bag no matter where the wind was, so a ship running free
 * and a ship stalled head-to-wind wore identical sails. This module is the response.
 *
 * THE TECHNIQUE, and why:
 *
 *   1. The sails are NOT re-authored (no Meshy, no Blender). Everything the shader needs — where the cloth is
 *      free, how deep the baked bag already is, which panel a vertex belongs to — is DERIVED ON THE CPU from
 *      the rest pose, ONCE per class, and baked into one `vec4` vertex attribute on the shared template
 *      geometry ({@link bakeSailRig}). Every clone of that class reuses it: zero per-ship geometry, zero
 *      per-frame CPU.
 *
 *   2. The mesh is UNWELDED (the brigantine's canvas is 240 disconnected 4-vertex quads). Any displacement
 *      that is not a PURE FUNCTION OF THE REST POSITION tears it into confetti — coincident corners of
 *      neighbouring quads must receive bit-identical offsets. So there is no per-vertex randomness anywhere
 *      in here: the rig attribute is computed from position, and the shader's noise is driven by (u, v) and
 *      a PER-PANEL phase, never by a vertex index. Welding the corners at bake time is what makes the panel
 *      analysis (and therefore the identical offsets) possible.
 *
 *   3. The displacement is authored in SHIP SPACE (+X bow, +Y up, +Z port) and converted to the sail mesh's
 *      own space by a constant `mat3` baked per class — so the maths reads like sailing ("the bag blows to
 *      leeward") instead of like a mesh-local axis puzzle, and it stays correct for any class whose GLB
 *      happens to be authored on a different axis.
 *
 *   4. It composes WITH the Blender furl morph instead of replacing it: the patch is injected after
 *      `<morphtarget_vertex>`, so the clip still owns the FURL and the shader owns the RESPONSE (bag depth,
 *      leeward skew, flogging, tear).
 *
 * PER-SHIP state (wind fill, luff, heel spring) lives in {@link SailState}; the drive maths is the pure,
 * unit-tested {@link sailEnv}. Heel is MESH-ONLY — it is added to the render group's roll and never reaches
 * the sim, the OBB or the wire (invariants 1, 2, 4).
 */
import * as THREE from 'three';

/* ------------------------------------------------------------------------------------------------------ *
 *  TUNING — every knob the sails' behaviour hangs on. Render-only, so it lives next to the code using it.
 * ------------------------------------------------------------------------------------------------------ */
export const SAIL_ART = {
  /** How much DEEPER than the baked rest bag a fully-drawing sail bellies (fraction of the baked depth). */
  DRAW_GAIN: 0.42,
  /** …and how much deeper still at full storm: a gale does not just blow harder, it STRAINS the canvas. */
  STORM_STRAIN: 0.5,
  /** Leeward skew of the bag on a reach, as a fraction of the panel's span. */
  SKEW: 0.20,
  /** Flogging amplitude as a fraction of the panel span: base (luffing) and the extra a full gale adds. */
  FLUTTER_BASE: 0.075,
  FLUTTER_STORM: 0.045,
  /** The two incommensurate flap rates (rad/s). Not harmonics — that is what stops it looking like a loop. */
  FLAP_W1: 8.5,
  FLAP_W2: 13.1,
  /** How hard the flogging tilts the shaded normal (so the beat CATCHES THE LIGHT, not just the silhouette). */
  NORMAL_GAIN: 1.25,
  /** Peak heel angle (rad) at full press. ~6° — a lean you feel, never a capsize. */
  HEEL_MAX: 0.105,
  /** Heel spring: stiffness and the damping it is paired with (critically damped at c = 2·sqrt(k)). */
  HEEL_K: 7.0,
  HEEL_C: 5.3,
  /** Storm multiplier on the heel target (a gale lays her over harder). */
  HEEL_STORM: 0.55,
  /** Backlit canvas: how much sun bleeds THROUGH the cloth when the sun is behind it. */
  GLOW: 0.85,
  /** Chain-shot damage: holes open above this fraction of the mask, and the torn cloth flogs this much extra. */
  TEAR_FLUTTER: 0.9,
} as const;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smoothstep = (a: number, b: number, x: number): number => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const normAngle = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

/* ------------------------------------------------------------------------------------------------------ *
 *  THE DRIVE MATHS — pure, so it can be unit-tested without a GL context.
 * ------------------------------------------------------------------------------------------------------ */

/** What the wind is doing to one ship's canvas this frame. All render-only. */
export interface SailDrive {
  /** 0..1 — how hard the canvas is DRAWING (bag deepens, ship pulls). */
  fill: number;
  /** 0..1 — how hard it is LUFFING (bag collapses, cloth flogs). */
  luff: number;
  /** −1..1 — the wind's cross-ship component, +1 = pushing toward PORT (bag + heel go that way). */
  lateral: number;
  /** Heel angle the hull is being pressed to (rad, + = mast to port). Cap already applied. */
  heelTarget: number;
}

/**
 * Read the wind exactly the way the SIM does and turn it into what the canvas should look like.
 *
 * `windFactor` in `@piratas/sim` scores a heading by `cos(heading − windDir)`: +1 running dead downwind
 * (fastest), −1 dead into it (in irons). This uses the SAME quantity, so the sail that looks full is
 * the sail that is actually pushing the boat — the visual can never lie about the physics.
 *
 * Pure: no wall-clock, no allocation, no THREE. `windDir` is where the wind blows TOWARD (sim convention).
 */
export function sailEnv(
  heading: number, windDir: number, windStrength: number,
  trim: number, rigging: number, storm: number,
): SailDrive {
  const rel = normAngle(windDir - heading);
  const along = Math.cos(rel);     // +1 = wind dead astern (running), −1 = dead ahead (in irons)
  const lateral = Math.sin(rel);   // +1 = wind pushing toward port
  const t = clamp01(trim);
  const w = Math.max(0, Math.min(1.6, windStrength));
  const rig = clamp01(rigging);

  // A square rig draws with the wind abaft the beam and dies as it comes forward of it. The band is
  // deliberately wider than a real rig's — this is an arcade sea, and a sail that snaps between full and
  // flogging on a 1° heading change reads as a bug, not as sailing.
  const draw = smoothstep(-0.42, 0.34, along);

  // Chain shot: torn rigging cannot hold the canvas to the wind, so it draws less and flogs more.
  const fill = clamp01(t * Math.min(1, w) * draw * (1 - 0.6 * rig));
  // Flogging needs wind. A sail set in a calm just hangs (the small floor), a sail aback beats itself apart.
  const luff = clamp01(t * Math.max(0.1 * t, (1 - draw) * Math.min(1.4, w)) + SAIL_ART.TEAR_FLUTTER * rig * t * 0.5);

  // Heeling moment: biggest on a beam reach, ~nothing running dead downwind (the press is all forward),
  // and small in irons (there is no press at all). A gale lays her over harder.
  const press = t * Math.min(1.5, w) * (0.3 + 0.7 * draw) * (1 - 0.45 * rig);
  const heel = SAIL_ART.HEEL_MAX * lateral * press * (1 + SAIL_ART.HEEL_STORM * clamp01(storm));
  const heelTarget = Math.max(-SAIL_ART.HEEL_MAX, Math.min(SAIL_ART.HEEL_MAX, heel));

  return { fill, luff, lateral, heelTarget };
}

/** Per-ship sail state: the smoothed drive + the heel spring. One per rendered hull (local AND remotes). */
export class SailState {
  fill = 0; luff = 0; lateral = 0;
  /** Applied heel (rad) and its velocity — a critically damped spring so the hull LEANS instead of snapping. */
  heel = 0; private heelV = 0;

  /** Advance toward this frame's drive. `dt` is clamped internally (a tab-out must not fling the spring). */
  step(d: SailDrive, dt: number): void {
    const h = Math.max(0.001, Math.min(0.1, dt));
    // Cloth is not symmetric. A sail DUMPS its wind the instant you point wrong and takes a beat to fill again
    // — so filling (and settling down from a flog) uses the slow rate, collapsing (and breaking into a flog)
    // uses the fast one. Getting this backwards is what makes canvas look like rubber.
    const slow = 1 - Math.pow(0.02, h), fast = 1 - Math.pow(0.0008, h);
    this.fill += (d.fill - this.fill) * (d.fill > this.fill ? slow : fast);
    this.luff += (d.luff - this.luff) * (d.luff > this.luff ? fast : slow);
    this.lateral += (d.lateral - this.lateral) * slow;
    this.heelV += ((d.heelTarget - this.heel) * SAIL_ART.HEEL_K - this.heelV * SAIL_ART.HEEL_C) * h;
    this.heel += this.heelV * h;
  }
}

/* ------------------------------------------------------------------------------------------------------ *
 *  BAKE — the cloth rig, derived from the rest pose, once per class.
 * ------------------------------------------------------------------------------------------------------ */

/** Constant, per-class facts the sail shader needs. Shared by every clone of that class. */
export interface SailRig {
  /** SHIP space (+X bow, +Y up, +Z port) → the sail mesh's own space. */
  shipToSail: THREE.Matrix3;
  /** Mean panel width in ship metres — the yardstick every amplitude in the shader is a fraction of. */
  span: number;
  /** The mesh→ship uniform scale. `shipToSail` also DIVIDES by it (it converts lengths, which is exactly
   *  right for a displacement and exactly wrong for a normal), so the shader multiplies it back in when it
   *  tilts the shaded normal. Without this the flap perturbs the normal ~8× too little and stops reading. */
  meshScale: number;
  /** How many separate sails were found (diagnostics / the report). */
  panels: number;
}

/**
 * Derive the cloth rig for one sail mesh and bake it into an `aSailRig` vec4 attribute.
 *
 *   .x = u    — 0..1 across the panel's span (0 and 1 are the LEECHES, the free vertical edges)
 *   .y = v    — 0 at the HEAD (lashed to the yard, immovable) → 1 at the FOOT (free)
 *   .z = bag  — this vertex's existing bulge, in ship metres, measured from the panel's head plane. The
 *               shader deepens it to fill and subtracts it to collapse, so "flat" is exact, not a guess.
 *   .w = phase— a per-PANEL constant so two sails on the same hull never flog in lockstep.
 *
 * `sailToShip` is the sail mesh's matrix relative to the ship group (i.e. `mesh.matrixWorld` while the
 * template is unparented), which is what makes the whole analysis happen in ship space.
 *
 * Idempotent: a geometry that already carries `aSailRig` is returned untouched.
 */
export function bakeSailRig(geo: THREE.BufferGeometry, sailToShip: THREE.Matrix4): SailRig | null {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!pos) return null;
  const n = pos.count;
  if (n < 12) return null;

  const cached = geo.userData.sailRig as SailRig | undefined;
  if (cached && geo.getAttribute('aSailRig')) return cached;

  // --- 1. every vertex into SHIP space (so u/v/bag are metres a sailor would recognise) ---
  const P = new Float32Array(n * 3);
  const v3 = new THREE.Vector3();
  for (let i = 0; i < n; i++) {
    v3.fromBufferAttribute(pos, i).applyMatrix4(sailToShip);
    P[i * 3] = v3.x; P[i * 3 + 1] = v3.y; P[i * 3 + 2] = v3.z;
  }

  // --- 2. weld coincident corners, then union across triangles → one component per SAIL ---
  // The GLB ships the canvas as loose quads; without the weld every quad is its own "panel" and the whole
  // analysis collapses. The weld tolerance is derived from the mesh's own edge length, never hardcoded.
  const idx = geo.getIndex();
  let edgeSum = 0, edgeN = 0;
  if (idx) {
    const N = Math.min(idx.count, 900);
    for (let i = 0; i + 2 < N; i += 3) {
      const a = idx.getX(i), b = idx.getX(i + 1);
      const dx = P[a * 3]! - P[b * 3]!, dy = P[a * 3 + 1]! - P[b * 3 + 1]!, dz = P[a * 3 + 2]! - P[b * 3 + 2]!;
      edgeSum += Math.hypot(dx, dy, dz); edgeN++;
    }
  }
  const edge = edgeN ? edgeSum / edgeN : 0.5;
  const eps = Math.max(1e-4, edge * 0.2);

  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (a: number): number => { while (parent[a] !== a) { parent[a] = parent[parent[a]!]!; a = parent[a]!; } return a; };
  const union = (a: number, b: number): void => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

  // spatial hash weld (O(n)) — coincident corners of neighbouring quads become one node
  const cell = Math.max(eps * 2, 1e-3);
  const bucket = new Map<string, number>();
  for (let i = 0; i < n; i++) {
    const k = `${Math.round(P[i * 3]! / cell)},${Math.round(P[i * 3 + 1]! / cell)},${Math.round(P[i * 3 + 2]! / cell)}`;
    const prev = bucket.get(k);
    if (prev === undefined) bucket.set(k, i); else union(i, prev);
  }
  if (idx) for (let i = 0; i + 2 < idx.count; i += 3) { union(idx.getX(i), idx.getX(i + 1)); union(idx.getX(i + 1), idx.getX(i + 2)); }
  else for (let i = 0; i + 2 < n; i += 3) { union(i, i + 1); union(i + 1, i + 2); }

  // --- 3. per-panel bounds ---
  interface Panel { yMin: number; yMax: number; zMin: number; zMax: number; headSum: number; headN: number; headX: number; phase: number; }
  const panels = new Map<number, Panel>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    let p = panels.get(r);
    if (!p) { p = { yMin: 1e9, yMax: -1e9, zMin: 1e9, zMax: -1e9, headSum: 0, headN: 0, headX: 0, phase: 0 }; panels.set(r, p); }
    const y = P[i * 3 + 1]!, z = P[i * 3 + 2]!;
    if (y < p.yMin) p.yMin = y; if (y > p.yMax) p.yMax = y;
    if (z < p.zMin) p.zMin = z; if (z > p.zMax) p.zMax = z;
  }
  // the HEAD PLANE: the mean fore-and-aft position of the top band of each panel. That band is lashed to the
  // yard, so it is the only part of a sail that is genuinely straight — the honest zero for "how bagged is this".
  for (let i = 0; i < n; i++) {
    const p = panels.get(find(i))!;
    const h = p.yMax - p.yMin;
    if (h > 1e-6 && P[i * 3 + 1]! > p.yMax - h * 0.14) { p.headSum += P[i * 3]!; p.headN++; }
  }
  let k = 0, spanSum = 0;
  for (const p of panels.values()) {
    p.headX = p.headN ? p.headSum / p.headN : 0;
    p.phase = (k * 2.39996) % 6.28318;  // golden angle — never lines two panels up
    spanSum += p.zMax - p.zMin;
    k++;
  }
  const span = panels.size ? spanSum / panels.size : 1;

  // --- 4. bake ---
  const rig = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const p = panels.get(find(i))!;
    const x = P[i * 3]!, y = P[i * 3 + 1]!, z = P[i * 3 + 2]!;
    const w = p.zMax - p.zMin, h = p.yMax - p.yMin;
    rig[i * 4] = w > 1e-6 ? (z - p.zMin) / w : 0.5;        // u across the span
    rig[i * 4 + 1] = h > 1e-6 ? (p.yMax - y) / h : 0.5;    // v: 0 head → 1 foot
    rig[i * 4 + 2] = x - p.headX;                          // bag, in ship metres, signed
    rig[i * 4 + 3] = p.phase;
  }
  geo.setAttribute('aSailRig', new THREE.BufferAttribute(rig, 4));

  const shipToSail = new THREE.Matrix3().setFromMatrix4(sailToShip).invert();
  const meshScale = new THREE.Vector3().setFromMatrixColumn(sailToShip, 0).length() || 1;
  const out: SailRig = { shipToSail, span: Math.max(0.5, span), meshScale, panels: panels.size };
  geo.userData.sailRig = out;
  return out;
}

/* ------------------------------------------------------------------------------------------------------ *
 *  SHADER — one patch on the existing sail material. No new draw call, ever.
 * ------------------------------------------------------------------------------------------------------ */

/** The live uniforms of ONE ship's canvas. `drive`/`fx` are per-ship; the rest are shared holders. */
export interface SailUniforms {
  /** x = fill, y = luff, z = lateral, w = trim. */
  drive: { value: THREE.Vector4 };
  /** x = time (s), y = storm 0..1, z = rigging damage 0..1, w = quality (1 full / 0 simplified). */
  fx: { value: THREE.Vector4 };
}

/** Uniform holders shared by EVERY sail (and pennant) in the scene: one object, N materials, one write a frame. */
export interface SailShared {
  sun: { value: THREE.Vector3 };    // VIEW-space direction toward the sun
  warm: { value: THREE.Color };     // the sun's colour, scaled by how much of it is left (drives the bleed)
  glow: { value: number };
  pennantT: { value: number };      // clock for the masthead streamers
}

export function makeSailShared(): SailShared {
  return {
    sun: { value: new THREE.Vector3(0, 1, 0) },
    warm: { value: new THREE.Color(0xfff2d8) },
    glow: { value: SAIL_ART.GLOW },
    pennantT: { value: 0 },
  };
}

const A = SAIL_ART;

/**
 * Patch ONE sail material so it answers the wind. The material must already be per-ship (clone it first) —
 * the uniform holder returned here is captured by the `onBeforeCompile` closure, so ANY later clone of this
 * material (the cosmetic tint, the ghost-fleet skin) keeps pointing at the SAME ship's uniforms, which is
 * exactly what we want and the reason this is a closure rather than `material.userData`.
 *
 * The injected GLSL is byte-identical for every ship, so three's program cache (keyed on
 * `onBeforeCompile.toString()`) compiles it ONCE and every hull shares the program.
 */
export function patchSailMaterial(
  mat: THREE.Material, rig: SailRig, shared: SailShared,
): SailUniforms {
  const u: SailUniforms = {
    drive: { value: new THREE.Vector4(0, 0, 0, 0) },
    fx: { value: new THREE.Vector4(0, 0, 0, 1) },
  };
  const shipToSail = { value: rig.shipToSail };
  const span = { value: rig.span };
  // uShipToSail converts LENGTHS (ship metres -> mesh units), which is right for the displacement and wrong
  // for the normal tilt — a normal only cares about the RATIO to the unit normal it is perturbing. Folding
  // the mesh scale back in here keeps the tilt in the same units as the geometry it is bending.
  const nGain = { value: (rig.meshScale * A.NORMAL_GAIN) / Math.max(0.35, rig.span) };

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uSailDrive = u.drive;
    shader.uniforms.uSailFx = u.fx;
    shader.uniforms.uShipToSail = shipToSail;
    shader.uniforms.uSailSpan = span;
    shader.uniforms.uSailNGain = nGain;
    shader.uniforms.uSailSun = shared.sun;
    shader.uniforms.uSailWarm = shared.warm;
    shader.uniforms.uSailGlow = shared.glow;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', /* glsl */`
#include <common>
attribute vec4 aSailRig;          // u, v(head→foot), bag(ship m), panel phase
uniform mat3  uShipToSail;        // ship space (+X bow, +Y up, +Z port) → this mesh's space
uniform float uSailSpan;          // mean panel width, ship metres
uniform float uSailNGain;         // normal-tilt gain (mesh scale folded back in — see patchSailMaterial)
uniform vec4  uSailDrive;         // fill, luff, lateral, trim
uniform vec4  uSailFx;            // time, storm, damage, quality
varying vec3  vSailRig;           // u, v, "how free is this cloth" (the fragment tear mask reads it)

/** The whole cloth response, in SHIP space. Returns the offset; writes the (u,v) flap gradient to \`grad\`. */
vec3 ccSailCloth(out vec2 grad) {
  float u = aSailRig.x, v = aSailRig.y, bag = aSailRig.z, ph = aSailRig.w;
  float fill = uSailDrive.x, luff = uSailDrive.y, lat = uSailDrive.z, trim = uSailDrive.w;
  float time = uSailFx.x, storm = uSailFx.y, dmg = uSailFx.z, q = uSailFx.w;

  // how free this scrap of cloth is: nailed along the head, loosest at the clews (the bottom corners)
  float leech = abs(u * 2.0 - 1.0);
  float free  = v * (0.30 + 0.70 * leech * leech);
  vSailRig = vec3(u, v, free);
  grad = vec2(0.0);

  vec3 d = vec3(0.0);
  // (1) DRAWING — the bag the artist baked, deepened by the press (harder in a gale) and blown to leeward.
  d.x += bag * ${A.DRAW_GAIN.toFixed(3)} * fill * (1.0 + ${A.STORM_STRAIN.toFixed(2)} * storm);
  d.z += -lat * fill * ${A.SKEW.toFixed(3)} * uSailSpan * clamp(bag / max(0.35, uSailSpan * 0.35), -1.0, 1.0);
  // (2) LUFFING — the bag COLLAPSES onto the chord plane. \`bag\` is measured, so luff = 1 is exactly flat.
  d.x -= bag * luff;
  if (q < 0.5) return d;   // Low preset: shape only, no flogging (uniform branch — coherent for the whole draw)

  // (3) FLOGGING — two incommensurate beats so the cloth never finds a loop, weighted by how free it is.
  float amp = uSailSpan * (${A.FLUTTER_BASE.toFixed(3)} * luff + ${A.FLUTTER_STORM.toFixed(3)} * storm * fill)
            * free * trim * (1.0 + dmg);
  float a1 = time * ${A.FLAP_W1.toFixed(2)} + ph + v * 5.1 + u * 2.3;
  float a2 = time * ${A.FLAP_W2.toFixed(2)} + ph * 1.7 - v * 3.3 + u * 4.7;
  float s1 = sin(a1), s2 = sin(a2), c1 = cos(a1), c2 = cos(a2);
  float flap = s1 * 0.65 + s2 * 0.35;
  d.x += flap * amp;
  d.z += (s2 - s1) * amp * 0.45;
  d.y -= abs(flap) * amp * 0.28;            // cloth bagging sideways cannot also reach as far down
  grad = vec2(c1 * 0.65 * 2.3 + c2 * 0.35 * 4.7, c1 * 0.65 * 5.1 - c2 * 0.35 * 3.3) * amp;
  return d;
}
`)
      // computed once, at the top of main(), so both the normal and the position can use it
      .replace('#include <beginnormal_vertex>', /* glsl */`
vec2 ccGrad;
vec3 ccSailD = ccSailCloth(ccGrad);
#include <beginnormal_vertex>
`)
      // tilt the shaded normal with the flap gradient — this is what makes the beat CATCH THE LIGHT
      .replace('#include <morphnormal_vertex>', /* glsl */`
#include <morphnormal_vertex>
objectNormal = normalize(objectNormal + (uShipToSail * vec3(0.0, -ccGrad.y, -ccGrad.x)) * uSailNGain);
`)
      // …and after the furl morph, so the Blender clip still owns the FURL and this owns the RESPONSE
      .replace('#include <morphtarget_vertex>', /* glsl */`
#include <morphtarget_vertex>
transformed += uShipToSail * ccSailD;
`);

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', /* glsl */`
#include <common>
uniform vec4 uSailFx;
uniform vec3 uSailSun, uSailWarm;
uniform float uSailGlow;
varying vec3 vSailRig;
float ccHash(vec2 p) { return fract(sin(dot(p, vec2(41.7, 289.3))) * 43758.5453); }
float ccNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(ccHash(i), ccHash(i + vec2(1.0, 0.0)), f.x),
             mix(ccHash(i + vec2(0.0, 1.0)), ccHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
`)
      // SHOT CANVAS — holes, opened by chain shot, biggest where the cloth already flogs. Behind a uniform
      // branch, so an undamaged rig (i.e. almost every sail on screen) pays nothing but the compare.
      .replace('#include <clipping_planes_fragment>', /* glsl */`
#include <clipping_planes_fragment>
if (uSailFx.z > 0.002) {
  float dmg = uSailFx.z;
  float m = ccNoise(vSailRig.xy * vec2(9.0, 7.0)) * 0.65 + ccNoise(vSailRig.xy * vec2(23.0, 19.0)) * 0.35;
  // shredded at the leeches and the clews, near-intact along the head — a shot rig fails where the cloth was
  // already free to whip, never in the bolt-roped band lashed to the yard.
  if (m < dmg * (0.10 + 0.28 * vSailRig.z)) discard;
}
`)
      // BACKLIT CANVAS — a sail with the sun behind it glows like paper. Two dots and a pow; it is the
      // cheapest thing in the frame that makes cloth read as cloth (and it lands on the golden hour).
      .replace('#include <dithering_fragment>', /* glsl */`
{
  // \`normal\` is the shading normal (view space, already flipped for the back face of a double-sided sail);
  // \`vViewPosition\` points from the fragment TOWARD the camera. \`uSailSun\` points toward the sun, view space.
  vec3 vV = normalize(vViewPosition);
  float thru = clamp(-dot(normal, uSailSun), 0.0, 1.0);       // the sun is on the FAR face of the cloth…
  float into = pow(clamp(-dot(vV, uSailSun), 0.0, 1.0), 3.0); // …and we are looking back into it
  float thin = 0.35 + 0.65 * (1.0 - vSailRig.z);              // the bag's belly is the thinnest part
  gl_FragColor.rgb += uSailWarm * (thru * into * thin * uSailGlow);
  if (uSailFx.z > 0.002) gl_FragColor.rgb *= 1.0 - 0.28 * uSailFx.z * vSailRig.z; // scorched round the rips
}
#include <dithering_fragment>
`);
  };
  mat.needsUpdate = true;
  return u;
}
